import {
  Box,
  Flex,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  Text,
  Portal,
} from '@chakra-ui/react';
import {
  CommunicatingEntityDetail,
  CommunicationDirection,
} from '../../types/entityCommunication';
import { EntityCommunicationRow } from './EntityCommunicationRow';
import { useEntityCommunicationPopover } from '../../hooks';
import { i18n } from '@investigator/shared/localization';

const modalCopy = i18n.pages.entities.entityAnalysisModal;

export interface EntityCommunicationPopoverProps {
  direction: CommunicationDirection;
  entities: CommunicatingEntityDetail[];
  isOpen: boolean;
  onClose: () => void;
}

export const EntityCommunicationPopover = ({
  direction,
  entities,
  isOpen,
  onClose,
}: EntityCommunicationPopoverProps): JSX.Element => {
  const { title, columns, placement, triggerPosition } =
    useEntityCommunicationPopover({ direction });

  return (
    <Popover
      isOpen={isOpen}
      onClose={onClose}
      placement={placement}
      isLazy
      closeOnBlur
    >
      <PopoverTrigger>
        <Box position="absolute" top="100%" w={0} h={0} {...triggerPosition} />
      </PopoverTrigger>
      <Portal>
        <PopoverContent
          minW="400px"
          maxW="600px"
          w="fit-content"
          maxH="280px"
          overflowY="auto"
          zIndex={1500}
        >
          <PopoverHeader
            borderBottom="1px solid"
            borderColor="border.layer.1"
            fontWeight="semibold"
          >
            {title}
          </PopoverHeader>
          <PopoverBody>
            {entities.length > 0 ? (
              <Flex direction="column">
                <Flex
                  w="100%"
                  py={2}
                  borderBottom="1px solid"
                  borderColor="border.layer.1"
                  display="grid"
                  gridTemplateColumns="80px 1fr 70px 50px"
                  gap={8}
                >
                  <Text
                    fontSize="sm"
                    lineHeight="5"
                    fontWeight="semibold"
                    color="text.secondary"
                    textAlign="left"
                  >
                    {columns.timestamp}
                  </Text>
                  <Text
                    fontSize="sm"
                    lineHeight="5"
                    fontWeight="semibold"
                    color="text.secondary"
                    textAlign="left"
                  >
                    {columns.entity}
                  </Text>
                  <Text
                    fontSize="sm"
                    lineHeight="5"
                    fontWeight="semibold"
                    color="text.secondary"
                    whiteSpace="nowrap"
                    textAlign="left"
                  >
                    {columns.portProtocol}
                  </Text>
                  <Text
                    fontSize="sm"
                    lineHeight="5"
                    fontWeight="semibold"
                    color="text.secondary"
                    textAlign="left"
                  >
                    {columns.count}
                  </Text>
                </Flex>
                {entities.map((entity, index) => (
                  <EntityCommunicationRow
                    key={`${entity.entityIp}-${index}`}
                    entity={entity}
                    isLast={index === entities.length - 1}
                  />
                ))}
              </Flex>
            ) : (
              <Text
                color="text.secondary"
                fontSize="sm"
                textAlign="center"
                py={4}
              >
                {modalCopy.entityCommunication.popover.noData}
              </Text>
            )}
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
